/**
 * Experience mode: Simple or Advanced.
 *
 * **Advanced is the default.** Jay: *"could we make sure advanced mode is the
 * default on as well for the settings?"* Simple was chosen as the default on
 * the theory that a less technical relative should meet the smallest possible
 * app; what actually happened is that everybody's first run hid the globe, the
 * timeline, the calendar and the bell, and the only way to find out they
 * existed was to go looking in Settings for a switch you had no reason to
 * suspect. A feature nobody knows about is not simpler, it is missing. Simple
 * stays — it is a real answer for a grandparent who wants the tree and nothing
 * else — but it is now something you choose rather than something you are
 * given.
 *
 * This file is the single place that decides *what* each mode includes. Every
 * screen asks `useFeature('…')` (see AppState) instead of checking the mode
 * itself, so adding a new feature later means: add a `Feature` name below, list
 * it in `ADVANCED_ONLY` if Simple should not see it, and guard its UI and its
 * route with that name. Nothing else needs to know about modes.
 */
export type ExperienceMode = 'simple' | 'advanced';

export const DEFAULT_EXPERIENCE: ExperienceMode = 'advanced';
export const EXPERIENCE_MODES: readonly ExperienceMode[] = ['simple', 'advanced'] as const;

/** Every switchable piece of the app. A feature not listed in ADVANCED_ONLY is on in both modes. */
export type Feature =
  // Dock tabs
  | 'tab.world'
  | 'tab.calendar'
  | 'tab.timeline'
  | 'tab.passedDown'
  // Floating icons
  | 'notifications'
  // Family tree strip
  | 'tree.legend'
  // My profile panel
  | 'profile.helping'
  | 'profile.uploads'
  | 'profile.drafts';

const ADVANCED_ONLY: ReadonlySet<Feature> = new Set<Feature>([
  'tab.world',
  'tab.calendar',
  'tab.timeline',
  'tab.passedDown',
  'notifications',
  'tree.legend',
  'profile.helping',
  'profile.uploads',
  'profile.drafts',
]);

export function isFeatureOn(mode: ExperienceMode, feature: Feature): boolean {
  return mode === 'advanced' || !ADVANCED_ONLY.has(feature);
}

export function isExperienceMode(v: unknown): v is ExperienceMode {
  return v === 'simple' || v === 'advanced';
}

/**
 * Screens that only exist in Advanced. A Simple user who lands on one of these
 * (an old bookmark, a typed address, a link in a notification) is sent to the
 * tree instead — see App. Add a new advanced-only screen here as well as in
 * the dock.
 */
const ROUTE_FEATURES: ReadonlyArray<{ pattern: RegExp; feature: Feature }> = [
  { pattern: /^\/world(\/|$)/, feature: 'tab.world' },
  { pattern: /^\/calendar(\/|$)/, feature: 'tab.calendar' },
  // The Timeline (all the family's stories, with the births along the line) is advanced-only; a single
  // story (/stories/:id) is reachable from the tree in both modes. /stories (the old address) is the same tab.
  { pattern: /^\/timeline(\/|$)/, feature: 'tab.timeline' },
  { pattern: /^\/stories\/?$/, feature: 'tab.timeline' },
  // Passed Down is a tab like the others. A single recipe (/recipes/:id) opens over the tree in both modes.
  { pattern: /^\/passed-down(\/|$)/, feature: 'tab.passedDown' },
];

/** The feature a path belongs to, when the path is mode-gated at all. */
export function featureForPath(path: string): Feature | null {
  return ROUTE_FEATURES.find((r) => r.pattern.test(path))?.feature ?? null;
}

/** Whether the mode may open this path. */
export function isPathAllowed(mode: ExperienceMode, path: string): boolean {
  const f = featureForPath(path);
  return f === null || isFeatureOn(mode, f);
}
